import React from 'react'
import { Helmet } from 'react-helmet'
import { withTranslation } from 'react-i18next'
import { animateScroll as scroll } from 'react-scroll'
import { connect } from 'react-redux'
import { compose } from 'redux'
import qs from 'qs'

import { getProjectEmbedCodeWithIFrame, getProjectEmbedCodeWithLoader } from 'utils/embed' // for cd
import { UTILS__ROUTER__add_query, UTILS__ROUTER__remove_query } from 'utils/router'
import {
    isSubscriptionExpired,
    isDeprecatedSubscription,
    isTrialSubscription,
    isEducation,
    isSubscriptionActivated,
} from 'common/utils/permissions.js'
import AnalyticsMonitor from 'common/services/AnalyticsMonitor'
import Toast from 'components/Toast/Toast'
import { isBlockArticleType } from 'common/utils/isBlockArticleType'
import { ORGANIZATIONS_REFRESH_MEMBERS, ORGANIZATIONS_SET_ME_AS_SELECTED_MEMBER } from 'store/actions'
import { API__PROJECTS } from 'api'
import { ProjectList } from './components/ProjectList/ProjectList'
import TrialProgressBanner from './components/TrialProgressBanner'
import OrganizationPanel from './components/OrganizationPanel/OrganizationPanel'
import TopBar from './components/TopBar/TopBar'
import ModalController, { MODAL_TYPES } from './modals/ModalController'
import { PROJECT_STATUS, TAB_TYPES, tryGetTabByQueryParam } from './utils/constants'
import ViewsCountBanner from './components/ViewsCountBanner/ViewsCountBanner'
import './MyProjects.scss'

const initialPagination = {
    initialPage: 0,
    perPage: 10,
    totalPages: 0,
    offsetPages: 0,
}

const initialActionLoading = {
    duplicateProject: false,
}

class MyProjects extends React.Component {
    constructor(props) {
        super(props)

        const { location, history } = this.props

        const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true })
        const { tab, isSuccess } = tryGetTabByQueryParam(queryParams.tab)
        const hasTabParam = !!queryParams.tab
        if (hasTabParam && !isSuccess)
            UTILS__ROUTER__remove_query('tab', {
                history: history,
                location: location,
            })

        this.state = {
            openedModals: [],
            actionLoading: initialActionLoading,
            tab: tab,
            isLoadingProjects: true,
            projects: {
                draft: {
                    count: null,
                    pagination: initialPagination,
                    filter: {
                        status: PROJECT_STATUS.draft,
                        sort: 'updated,desc',
                    },
                    content: null,
                },
                published: {
                    count: null,
                    pagination: initialPagination,
                    filter: {
                        status: PROJECT_STATUS.published,
                        sort: 'updated,desc',
                    },
                    content: null,
                },
            },
        }
    }

    async componentDidMount() {
        const { location, organizations } = this.props

        this.openDecisionOrgInviteModals(organizations.organizationList)

        const hasOnlyOwnOrg = organizations.organizationList.length === 1
        if (location.state) {
            await this.openPublishedModal(location)
        } else if (isDeprecatedSubscription() && isEducation()) {
            await this.onOpenModal(MODAL_TYPES.educationModal)
        } else if (
            isDeprecatedSubscription() ||
            (isSubscriptionExpired() && hasOnlyOwnOrg && isSubscriptionActivated())
        ) {
            await this.onOpenModal(MODAL_TYPES.subscriptionExpired)
        }

        await this.loadProjectsAndSetTab()
    }

    async componentDidUpdate(prevProps, prevState, snapshot) {
        const organizations = this.props.organizations
        const isMemberChanged = organizations.selectedMember !== prevProps.organizations.selectedMember
        const isOrgChanged = organizations.selectedOrganizationId !== prevProps.organizations.selectedOrganizationId
        if (isOrgChanged || isMemberChanged) {
            await this.loadProjectsAndSetTab()
        }
    }

    openDecisionOrgInviteModals = organizationList => {
        organizationList.forEach(async org => {
            if (!org.isApproved) {
                await this.onOpenModal(MODAL_TYPES.decisionOrganizationInviteModal, org)
            }
        })
    }

    loadProjectsAndSetTab = async (targetTab = null, resetPagination = true) => {
        const {
            organizations: { selectedOrganizationId, selectedMember },
        } = this.props

        await this.setState({ isLoadingProjects: true })
        try {
            await this.getProjectsCount()
            const tab = this.chooseTab(targetTab, this.state.tab, this.state.projects)
            const {
                projects: {
                    [tab]: { pagination, filter },
                },
            } = this.state
            const params = {}
            // Pagination
            const newPagination = resetPagination ? initialPagination : pagination

            params.perPage = newPagination.perPage
            params.offsetPages = newPagination.offsetPages
            // Filter
            params.status = filter.status
            params.sort = filter.sort
            params.authorId = selectedMember ? selectedMember.userId : null
            params.organizationId = selectedOrganizationId

            const projects = await API__PROJECTS.GET_PROJECTS(params)
            await this.setState(prevState => ({
                projects: {
                    ...prevState.projects,
                    [tab]: {
                        ...prevState.projects[tab],
                        content: projects.content,
                        pagination: {
                            ...prevState.projects[tab].pagination,
                            totalPages: projects.totalPages,
                        },
                    },
                },
            }))
            this.setTab(tab)
        } finally {
            await this.setState({ isLoadingProjects: false })
        }
    }

    chooseTab = (targetTab, stateTab, projects) => {
        const isNotDefinedYet = stateTab === null && targetTab === null
        if (isNotDefinedYet) {
            const hasPublishedProjects = projects.published.count > 0
            return hasPublishedProjects ? TAB_TYPES.published : TAB_TYPES.draft
        }
        if (targetTab) return targetTab

        return stateTab
    }

    setTab = tab => {
        if (this.state.tab === tab) return

        const { history, location } = this.props
        UTILS__ROUTER__add_query('tab', tab, {
            history: history,
            location: location,
        })
        this.setState({ tab })
    }

    getProjectsCount = async () => {
        const {
            organizations: { selectedOrganizationId, selectedMember },
        } = this.props
        try {
            const response1 = await API__PROJECTS.GET_PROJECTS_COUNT({
                authorId: selectedMember ? selectedMember.userId : null,
                organizationId: selectedOrganizationId,
            })

            await this.setState(prevState => ({
                projects: {
                    ...prevState.projects,
                    draft: {
                        ...prevState.projects.draft,
                        count: 0,
                    },
                    published: {
                        ...prevState.projects.published,
                        count: 0,
                    },
                },
            }))

            for (const el of response1) {
                switch (el.status) {
                    case PROJECT_STATUS.draft: {
                        await this.setState(prevState => ({
                            projects: {
                                ...prevState.projects,
                                draft: {
                                    ...prevState.projects.draft,
                                    count: el.count,
                                },
                            },
                        }))
                        break
                    }
                    case PROJECT_STATUS.published: {
                        await this.setState(prevState => ({
                            projects: {
                                ...prevState.projects,
                                published: {
                                    ...prevState.projects.published,
                                    count: el.count,
                                },
                            },
                        }))
                        break
                    }
                    case 'DELETED': {
                        // На будущее мб что-то можно будет с этим придумать
                        break
                    }
                    default:
                        break
                }
            }
        } catch (err) {
            Toast('error', {})
            throw err
        }
    }

    openPublishedModal = async location => {
        const { t, history } = this.props
        const { action } = location.state
        if (action && action === 'editor--published') {
            const { data } = location.state
            const payload = {
                title: t('Published'),
                projectUrl: data.projectUrl,
                embedCode: data.embedCode,
                iframeCode: data.iframeCode,
            }
            await this.onOpenModal(MODAL_TYPES.publishModal, payload)
        }
        history.replace({ search: location.search })
    }

    onChangeTab = async (targetTab, evt) => {
        if (evt) {
            evt.preventDefault()
        }

        const { tab } = this.state
        if (targetTab !== tab) {
            this.onScrollToTop()
            await this.loadProjectsAndSetTab(targetTab)
        }
    }

    onScrollToTop = () => {
        scroll.scrollToTop({
            duration: 400,
        })
    }

    onEditProject = async (project, evt) => {
        const { history } = this.props

        if (evt) {
            evt.preventDefault()
        }

        if (isSubscriptionExpired()) {
            await this.onOpenModal(MODAL_TYPES.subscriptionExpired)
            return
        }

        if (isBlockArticleType(project.projectType.projectType)) {
            AnalyticsMonitor.track('Project edited', { projectId: project.id })
            history.push(`/editor/${project.projectId}`)
        } else {
            history.push(`/editor?projectId=${project.projectId}&id=${project.id}`)
        }
    }

    onRenameProject = async (project, name) => {
        const { t } = this.props
        const { tab } = this.state

        await this.setState(prevState => ({
            projects: {
                ...prevState.projects,
                [tab]: {
                    ...prevState.projects[tab],
                    content: prevState.projects[tab].content.map(item =>
                        item.projectId === project.projectId ? { ...item, name } : item,
                    ),
                },
            },
        }))

        await API__PROJECTS.UPDATE_PROJECT(project.projectId, {
            id: project.id,
            projectId: project.projectId,
            name: name.trim(),
        }).catch(error => {
            Toast('error', {
                message: t(error.response.data.message),
            })
            console.error(error)
        })
    }

    onDeleteProject = async (project, evt) => {
        const { t, REFRESH_MEMBERS } = this.props

        if (evt) {
            evt.preventDefault()
        }

        try {
            await API__PROJECTS.DELETE_PROJECT(project.projectId)
            REFRESH_MEMBERS()
            switch (project.projectStatus) {
                case PROJECT_STATUS.published: {
                    const {
                        projects: {
                            published: { content },
                        },
                    } = this.state
                    await this.setState(prevState => ({
                        projects: {
                            ...prevState.projects,
                            published: {
                                ...prevState.projects.published,
                                content: content.map(item =>
                                    item.projectId === project.projectId ? { ...item, isRemoved: true } : item,
                                ),
                            },
                        },
                    }))
                    break
                }
                case PROJECT_STATUS.draft: {
                    const {
                        projects: {
                            draft: { content },
                        },
                    } = this.state
                    await this.setState(prevState => ({
                        projects: {
                            ...prevState.projects,
                            draft: {
                                ...prevState.projects.draft,
                                content: content.map(item =>
                                    item.projectId === project.projectId ? { ...item, isRemoved: true } : item,
                                ),
                            },
                        },
                    }))
                    break
                }
                default:
                    break
            }

            Toast('success', { message: t('Project was deleted successfully!') })
        } catch (error) {
            Toast('error', { message: t('Sorry, looks like there was some error. Please, try again later.') })
            console.error(error)
        } finally {
            await this.onCloseModal(MODAL_TYPES.deleteProjectModal)
        }
    }

    onDuplicateProject = async (data, evt) => {
        if (evt) {
            evt.preventDefault()
        }
        const { actionLoading } = this.state
        if (actionLoading.duplicateProject) {
            return
        }

        if (isSubscriptionExpired()) {
            this.onOpenModal(MODAL_TYPES.subscriptionExpired)
            return
        }

        try {
            const {
                t,
                user_details,
                organizations: { selectedMember },
                SET_ME_AS_SELECTED_MEMBER,
                REFRESH_MEMBERS,
            } = this.props

            await this.setState(prevState => ({
                actionLoading: {
                    ...prevState.actionLoading,
                    duplicateProject: true,
                },
            }))

            await API__PROJECTS.CLONE_PROJECT(data.id)

            REFRESH_MEMBERS()
            if (selectedMember && selectedMember.userId !== user_details.id) {
                SET_ME_AS_SELECTED_MEMBER()
            }

            this.onScrollToTop()
            await this.loadProjectsAndSetTab(TAB_TYPES.draft)

            await this.setState(prevState => ({
                actionLoading: {
                    ...prevState.actionLoading,
                    duplicateProject: false,
                },
            }))

            Toast('success', {
                message: t('Project successfully duplicated'),
            })
        } catch (err) {
            console.error(err)
            Toast('error', {})
        } finally {
            await this.setState({
                actionLoading: initialActionLoading,
            })
        }
    }

    onChangeProjectVisibility = async (project, isHidden) => {
        try {
            await API__PROJECTS.CHANGE_VISIBILITY({
                projectId: project.id,
                isHidden,
            })

            const { t } = this.props
            const { tab } = this.state
            this.setState(prevState => ({
                projects: {
                    ...prevState.projects,
                    [tab]: {
                        ...prevState.projects[tab],
                        content: prevState.projects[tab].content.map(item =>
                            item.projectId === project.projectId ? { ...item, hidden: isHidden } : item,
                        ),
                    },
                },
            }))
            Toast('success', {
                message: t('Project settings successfully updated'),
            })
        } catch (err) {
            Toast('error')
            console.error(err)
        }
    }

    onOpenModal = async (type, payload, evt) => {
        if (evt) {
            evt.preventDefault()
        }

        await this.setState(prevState => ({
            openedModals: [...prevState.openedModals, { type, payload }],
        }))
    }

    onCloseModal = async (type, evt) => {
        if (evt) {
            evt.preventDefault()
        }
        await this.setState(prevState => ({
            openedModals: prevState.openedModals.filter(modal => modal.type !== type),
        }))
    }

    onOpenShareModal = async (project, evt) => {
        if (evt) {
            evt.preventDefault()
        }

        if (isSubscriptionExpired()) {
            await this.onOpenModal(MODAL_TYPES.subscriptionExpired)
            return
        }

        const embedCode = getProjectEmbedCodeWithLoader(project.projectVersions[0].versionHash)
        const iframeCode = getProjectEmbedCodeWithIFrame(project.projectVersions[0].versionHash)

        const payload = {
            projectUrl: project.projectVersions[0].publicUrl,
            embedCode,
            iframeCode,
        }
        await this.onOpenModal(MODAL_TYPES.shareModal, payload)
    }

    onPaginationClick = targetTab => async data => {
        try {
            this.onScrollToTop()
            await this.setState(prevState => ({
                projects: {
                    ...prevState.projects,
                    [targetTab]: {
                        ...prevState.projects[targetTab],
                        pagination: {
                            ...prevState.projects[targetTab].pagination,
                            offsetPages: data.selected,
                        },
                    },
                },
            }))
            await this.loadProjectsAndSetTab(targetTab, false)
        } catch (err) {
            console.error(err)
        }
    }

    onCreateProject = async templateId => {
        if (isSubscriptionExpired()) {
            await this.onOpenModal(MODAL_TYPES.subscriptionExpired)
            return
        }
        const { REFRESH_MEMBERS } = this.props

        const fromTemplate = typeof templateId === 'number'
        const _templateId = fromTemplate ? templateId : process.env.REACT_APP_SCRATCH_TEMPLATE_ID

        API__PROJECTS.CREATE_PROJECT_FROM_TEMPLATE(_templateId)
            .then(project => {
                AnalyticsMonitor.track('Project created', {
                    templateId: _templateId,
                    projectId: project.id,
                    source: fromTemplate ? 'template' : 'scratch',
                })
                REFRESH_MEMBERS()
                this.props.history.push(`/editor/${project.projectId}`)
            })
            .catch(
                ({
                    response: {
                        status,
                        data: { message },
                    },
                }) => {
                    if (status === 403) {
                        Toast('error', { message })
                    }
                },
            )
    }

    render() {
        const {
            t,
            organizations: { selectedSubscription, selectedMember },
        } = this.props

        const { tab, isLoadingProjects, projects, openedModals, actionLoading } = this.state

        const isTrial = isTrialSubscription()
        const isPublishedTab = tab === TAB_TYPES.published
        return (
            <div className="page-projects">
                <Helmet>
                    <title>{t('My projects')} | Interacty</title>
                </Helmet>

                <OrganizationPanel
                    selectedMember={selectedMember}
                    onOpenModal={this.onOpenModal}
                    onScrollToTop={this.onScrollToTop}
                />

                <section className="page-projects__projects-block">
                    <TopBar
                        tab={tab}
                        projects={projects}
                        isLoadingProjects={isLoadingProjects}
                        onChangeTab={this.onChangeTab}
                        onCreateFromScratch={this.onCreateProject}
                    />
                    {selectedSubscription.started && (
                        <TrialProgressBanner
                            isVisible={isTrial}
                            startDate={selectedSubscription.started}
                            expirationDate={selectedSubscription.expirationDate}
                        />
                    )}
                    {isPublishedTab && <ViewsCountBanner subscription={selectedSubscription} isVisible={!isTrial} />}
                    <ProjectList
                        isLoading={isLoadingProjects}
                        tab={tab}
                        projects={projects}
                        actionLoading={actionLoading}
                        onOpenModal={this.onOpenModal}
                        onEditProject={this.onEditProject}
                        onOpenShareModal={this.onOpenShareModal}
                        onDuplicateProject={this.onDuplicateProject}
                        onChangeProjectVisibility={this.onChangeProjectVisibility}
                        onPaginationClick={this.onPaginationClick}
                        onRenameProject={this.onRenameProject}
                        onCreateProject={this.onCreateProject}
                    />
                </section>
                <ModalController
                    onOpenModal={this.onOpenModal}
                    openedModals={openedModals}
                    onCloseModal={this.onCloseModal}
                    onDeleteProject={this.onDeleteProject}
                />
            </div>
        )
    }
}

const mapStateToProps = state => ({
    user_details: state.user_details,
    organizations: state.organizations,
})

const mapDispatchToProps = dispatch => {
    return {
        REFRESH_MEMBERS: () => dispatch(ORGANIZATIONS_REFRESH_MEMBERS()),
        SET_ME_AS_SELECTED_MEMBER: () => dispatch(ORGANIZATIONS_SET_ME_AS_SELECTED_MEMBER()),
    }
}

export default compose(connect(mapStateToProps, mapDispatchToProps), withTranslation('translations'))(MyProjects)
